export function ProgressTracker({ journeySteps, learningProgress, onNavigate, theme }) {
  if (!journeySteps || journeySteps.length === 0) return null;

  const completedCount = journeySteps.filter(s => learningProgress?.[s.key]?.status === 'completed').length;
  const totalCount = journeySteps.length;
  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isComplete = completedCount >= totalCount && totalCount > 0;

  // Find the next incomplete step for "Quick Resume"
  const nextStep = journeySteps.find(s => learningProgress?.[s.key]?.status !== 'completed');

  const TYPE_ICONS = {
    legend: 'L', corner: 'C', border: 'B', warning: 'W', landmark: 'X',
  };

  return (
    <div className="mb-10 fade-up" data-testid="progress-tracker">
      <div className="max-w-md mx-auto mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] tracking-[0.2em] uppercase font-serif" style={{ color: theme?.accentPrimary ? `${theme.accentPrimary}90` : 'rgba(217,119,6,0.6)' }}>Scrolls Studied</span>
          <span className="text-sm font-mono" style={{ color: theme?.accentPrimary || '#d97706' }} data-testid="progress-count">{completedCount} / {totalCount}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: theme?.name === 'dark' ? 'rgba(120,53,15,0.25)' : 'rgba(217,119,6,0.12)' }}>
          <div className="h-full rounded-full transition-all duration-1000 ease-out" data-testid="progress-bar"
            style={{ width: `${progressPct}%`, background: isComplete ? 'linear-gradient(90deg, #d97706, #fbbf24, #d97706)' : 'linear-gradient(90deg, #92400e, #d97706)' }} />
        </div>
      </div>

      {/* Journey markers — clickable */}
      <div className="flex items-center justify-center gap-1 sm:gap-1.5 flex-wrap max-w-lg mx-auto">
        {journeySteps.map((step, idx) => {
          const isDone = learningProgress?.[step.key]?.status === 'completed';
          const isActive = learningProgress?.[step.key]?.status === 'in_progress';
          const isNext = nextStep?.key === step.key;
          const prevType = idx > 0 ? journeySteps[idx - 1].type : null;
          const showSep = prevType && prevType !== step.type;

          return (
            <div key={step.key} className="flex items-center gap-1 sm:gap-1.5">
              {showSep && <div className="w-px h-4 mx-0.5" style={{ background: 'rgba(120,80,20,0.15)' }} />}
              <button
                onClick={() => onNavigate(step.route)}
                data-testid={`seal-${step.key}`}
                className={`relative w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-125 hover:z-10 ${isDone ? 'seal-glow' : ''} ${isNext ? 'ring-2 ring-offset-1' : ''}`}
                style={{
                  background: isDone
                    ? `radial-gradient(circle, ${step.color}50, ${step.color}20)`
                    : isActive || isNext
                    ? `radial-gradient(circle, ${step.color}25, ${step.color}08)`
                    : 'rgba(30,20,10,0.5)',
                  border: `1.5px solid ${isDone ? step.color : isActive || isNext ? `${step.color}60` : 'rgba(120,80,20,0.15)'}`,
                  cursor: 'pointer',
                  ...(isNext ? { ringColor: `${step.color}50`, '--tw-ring-offset-color': 'rgba(6,5,3,1)' } : {}),
                }}
                title={`${step.label}${isDone ? ' (Complete)' : isActive ? ' (In Progress)' : isNext ? ' (Next)' : ''}`}
              >
                {isDone ? (
                  <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" viewBox="0 0 24 24" stroke={step.color} strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="text-[8px] sm:text-[9px] font-bold font-mono" style={{ color: isActive || isNext ? `${step.color}90` : 'rgba(120,80,20,0.35)' }}>
                    {TYPE_ICONS[step.type] || '\u00b7'}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Quick Resume — "Continue" button */}
      {nextStep && !isComplete && (
        <div className="text-center mt-4" data-testid="quick-resume">
          <button onClick={() => onNavigate(nextStep.route)}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold transition-all hover:scale-105 border"
            style={{
              background: `linear-gradient(135deg, ${nextStep.color}12, ${nextStep.color}06)`,
              borderColor: `${nextStep.color}35`,
              color: nextStep.color,
            }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: nextStep.color }} />
            Continue: {nextStep.label}
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {isComplete && (
        <p className="text-center mt-3 text-xs font-serif italic" style={{ color: theme?.accentPrimary ? `${theme.accentPrimary}b0` : 'rgba(217,119,6,0.7)' }} data-testid="completion-message">
          All scrolls have been studied. The map is complete.
        </p>
      )}
    </div>
  );
}
